import React, { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import TextAlign from '@tiptap/extension-text-align';
import Highlight from '@tiptap/extension-highlight';
import Underline from '@tiptap/extension-underline';
import { FileButton } from './FileButtonExtension';
import { Bold, Italic, Underline as UnderlineIcon, List, AlignLeft, AlignCenter, AlignRight, Link as LinkIcon, Heading1, Heading2, Heading3, Image as ImageIcon, File } from 'lucide-react';
import { useToast } from './Toast';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/config';

export function RichTextEditor({ value, onChange, placeholder = 'Start writing...' }) {
  const { showToast } = useToast();

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3, 4],
        },
        link: false, // Disable link from StarterKit since we're adding it separately
      }),
      Image.configure({
        inline: true,
        allowBase64: false,
        HTMLAttributes: {
          style: 'max-width: 100%; height: auto; border-radius: 4px;',
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          style: 'color: #c5a059; text-decoration: underline;',
        },
      }),
      FileButton, // Custom file button node extension
      TextStyle,
      Color,
      Underline,
      TextAlign.configure({
        types: ['heading', 'paragraph', 'fileButton'],
      }),
      Highlight.configure({
        multicolor: true,
      }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange(html);
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm sm:prose lg:prose-lg xl:prose-xl mx-auto focus:outline-none min-h-[300px] p-4',
        style: 'min-height: 300px;',
        'data-placeholder': placeholder,
      },
    },
  });

  // Update editor content when value prop changes
  useEffect(() => {
    if (editor && value !== undefined) {
      const currentContent = editor.getHTML();
      if (value !== currentContent) {
        editor.commands.setContent(value || '');
      }
    }
  }, [value, editor]);

  const handleImageUpload = async () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;

      // Validate size
      if (file.size > 5 * 1024 * 1024) {
        showToast('Image size must be less than 5MB', 'error');
        return;
      }

      try {
        // Upload to Firebase Storage
        const { storage } = await import('../firebase/config');
        const { ref, uploadBytes, getDownloadURL } = await import('firebase/storage');
        
        const filename = `${Date.now()}_${Math.random().toString(36).substring(2, 9)}_${file.name.replace(/[^a-zA-Z0-9.]/g, '_')}`;
        const storageRef = ref(storage, `course-images/${filename}`);
        
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);

        // Insert image into editor
        if (editor) {
          editor.chain().focus().setImage({ src: url }).run();
        }

        showToast('Image uploaded successfully', 'success');
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error('Upload error:', error);
        }
        showToast('Failed to upload image', 'error');
      }
    };
  };

  const setLink = () => {
    if (!editor) return;

    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL', previousUrl);

    if (url === null) {
      return;
    }

    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const handleFileUpload = async () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.click();

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;

      // Validate size (50MB max for SFTP)
      if (file.size > 50 * 1024 * 1024) {
        showToast('File size must be less than 50MB', 'error');
        return;
      }

      try {
        showToast('Uploading file to server...', 'info');
        
        // Read file as base64
        const reader = new FileReader();
        reader.onload = async (event) => {
          try {
            const base64Data = event.target.result.split(',')[1]; // Remove data:type;base64, prefix
            
            // Call Cloud Function to upload to SFTP
            const uploadFileToSFTP = httpsCallable(functions, 'uploadFileToSFTP');
            const result = await uploadFileToSFTP({
              fileData: base64Data,
              fileName: file.name,
              folder: 'course-files',
              mimeType: file.type
            });

            if (result.data.success) {
              // Insert file button using the custom FileButton node extension
              if (editor) {
                const fileUrl = result.data.publicUrl;
                const fileName = result.data.fileName;
                
                // Insert using the FileButton node extension (adheres to Tiptap schema)
                editor.chain().focus().insertContent({
                  type: 'fileButton',
                  attrs: {
                    url: fileUrl,
                    fileName: fileName,
                  },
                }).run();
              }

              showToast('File uploaded and inserted successfully', 'success');
            } else {
              throw new Error(result.data.error || 'Upload failed');
            }
          } catch (error) {
            console.error('Upload error:', error);
            let errorMessage = 'Failed to upload file';
            
            if (error.message?.includes('permission-denied')) {
              errorMessage = 'You are not authorized to upload files.';
            } else if (error.message?.includes('unauthenticated')) {
              errorMessage = 'Please log in to upload files.';
            } else {
              errorMessage = `Failed to upload file: ${error.message || 'Unknown error'}`;
            }
            
            showToast(errorMessage, 'error');
          }
        };
        
        reader.onerror = () => {
          showToast('Failed to read file', 'error');
        };
        
        reader.readAsDataURL(file);
      } catch (error) {
        console.error('File read error:', error);
        showToast('Failed to read file', 'error');
      }
    };
  };

  if (!editor) {
    return null;
  }

  return (
    <div className="border border-gray-300 rounded-lg overflow-hidden bg-white">
      {/* Formatting Toolbar */}
      <div className="flex flex-wrap items-center gap-1 p-2 bg-white border-b border-gray-200 rounded-lg overflow-x-auto">
        {/* Text Formatting */}
        <div className="flex items-center gap-1 border-r border-gray-200 pr-2 mr-2 flex-shrink-0">
          <button
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={`p-2 rounded hover:bg-gray-100 transition min-h-[44px] min-w-[44px] flex items-center justify-center ${
              editor.isActive('bold') ? 'bg-[#c5a059]/10 text-[#c5a059]' : 'text-gray-700'
            }`}
            title="Bold"
          >
            <Bold size={18} />
          </button>
          <button
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={`p-2 rounded hover:bg-gray-100 transition min-h-[44px] min-w-[44px] flex items-center justify-center ${
              editor.isActive('italic') ? 'bg-[#c5a059]/10 text-[#c5a059]' : 'text-gray-700'
            }`}
            title="Italic"
          >
            <Italic size={18} />
          </button>
          <button
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            className={`p-2 rounded hover:bg-gray-100 transition min-h-[44px] min-w-[44px] flex items-center justify-center ${
              editor.isActive('underline') ? 'bg-[#c5a059]/10 text-[#c5a059]' : 'text-gray-700'
            }`}
            title="Underline"
          >
            <UnderlineIcon size={18} />
          </button>
        </div>

        {/* Headings */}
        <div className="flex items-center gap-1 border-r border-gray-200 pr-2 mr-2 flex-shrink-0">
          <button
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
            className={`p-2 rounded hover:bg-gray-100 transition min-h-[44px] min-w-[44px] flex items-center justify-center ${
              editor.isActive('heading', { level: 1 }) ? 'bg-[#c5a059]/10 text-[#c5a059]' : 'text-gray-700'
            }`}
            title="Heading 1"
          >
            <Heading1 size={18} />
          </button>
          <button
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            className={`p-2 rounded hover:bg-gray-100 transition min-h-[44px] min-w-[44px] flex items-center justify-center ${
              editor.isActive('heading', { level: 2 }) ? 'bg-[#c5a059]/10 text-[#c5a059]' : 'text-gray-700'
            }`}
            title="Heading 2"
          >
            <Heading2 size={18} />
          </button>
          <button
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            className={`p-2 rounded hover:bg-gray-100 transition min-h-[44px] min-w-[44px] flex items-center justify-center ${
              editor.isActive('heading', { level: 3 }) ? 'bg-[#c5a059]/10 text-[#c5a059]' : 'text-gray-700'
            }`}
            title="Heading 3"
          >
            <Heading3 size={18} />
          </button>
        </div>

        {/* Lists */}
        <div className="flex items-center gap-1 border-r border-gray-200 pr-2 mr-2 flex-shrink-0">
          <button
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className={`p-2 rounded hover:bg-gray-100 transition min-h-[44px] min-w-[44px] flex items-center justify-center ${
              editor.isActive('bulletList') ? 'bg-[#c5a059]/10 text-[#c5a059]' : 'text-gray-700'
            }`}
            title="Bullet List"
          >
            <List size={18} />
          </button>
          <button
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className={`p-2 rounded hover:bg-gray-100 transition min-h-[44px] min-w-[44px] flex items-center justify-center ${
              editor.isActive('orderedList') ? 'bg-[#c5a059]/10 text-[#c5a059]' : 'text-gray-700'
            }`}
            title="Numbered List"
          >
            <List size={18} className="rotate-90" />
          </button>
        </div>

        {/* Alignment */}
        <div className="flex items-center gap-1 border-r border-gray-200 pr-2 mr-2 flex-shrink-0">
          <button
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
            className={`p-2 rounded hover:bg-gray-100 transition min-h-[44px] min-w-[44px] flex items-center justify-center ${
              editor.isActive({ textAlign: 'left' }) ? 'bg-[#c5a059]/10 text-[#c5a059]' : 'text-gray-700'
            }`}
            title="Align Left"
          >
            <AlignLeft size={18} />
          </button>
          <button
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
            className={`p-2 rounded hover:bg-gray-100 transition min-h-[44px] min-w-[44px] flex items-center justify-center ${
              editor.isActive({ textAlign: 'center' }) ? 'bg-[#c5a059]/10 text-[#c5a059]' : 'text-gray-700'
            }`}
            title="Align Center"
          >
            <AlignCenter size={18} />
          </button>
          <button
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
            className={`p-2 rounded hover:bg-gray-100 transition min-h-[44px] min-w-[44px] flex items-center justify-center ${
              editor.isActive({ textAlign: 'right' }) ? 'bg-[#c5a059]/10 text-[#c5a059]' : 'text-gray-700'
            }`}
            title="Align Right"
          >
            <AlignRight size={18} />
          </button>
        </div>

        {/* Media */}
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={handleImageUpload}
            className="p-2 rounded hover:bg-gray-100 transition text-gray-700 min-h-[44px] min-w-[44px] flex items-center justify-center"
            title="Insert Image"
          >
            <ImageIcon size={18} />
          </button>
          <button
            onClick={handleFileUpload}
            className="p-2 rounded hover:bg-gray-100 transition text-gray-700 min-h-[44px] min-w-[44px] flex items-center justify-center"
            title="Upload File (to SFTP)"
          >
            <File size={18} />
          </button>
          <button
            onClick={setLink}
            className={`p-2 rounded hover:bg-gray-100 transition min-h-[44px] min-w-[44px] flex items-center justify-center ${
              editor.isActive('link') ? 'bg-[#c5a059]/10 text-[#c5a059]' : 'text-gray-700'
            }`}
            title="Insert Link"
          >
            <LinkIcon size={18} />
          </button>
        </div>

        {/* Colors */}
        <div className="flex items-center gap-1 ml-auto sm:ml-auto border-l border-gray-200 pl-2 flex-shrink-0">
          <input
            type="color"
            onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
            className="w-10 h-10 sm:w-8 sm:h-8 rounded border border-gray-300 cursor-pointer min-h-[44px] min-w-[44px]"
            title="Text Color"
          />
          <input
            type="color"
            onChange={(e) => editor.chain().focus().toggleHighlight({ color: e.target.value }).run()}
            className="w-10 h-10 sm:w-8 sm:h-8 rounded border border-gray-300 cursor-pointer min-h-[44px] min-w-[44px]"
            title="Highlight Color"
          />
        </div>
      </div>

      {/* Editor Content */}
      <div className="min-h-[300px] max-h-[600px] overflow-y-auto">
        <EditorContent editor={editor} />
      </div>

      <style>{`
        .ProseMirror {
          outline: none;
        }
        .ProseMirror p.is-editor-empty:first-child::before {
          color: #adb5bd;
          content: attr(data-placeholder);
          float: left;
          height: 0;
          pointer-events: none;
        }
        .ProseMirror img {
          max-width: 100%;
          height: auto;
        }
        .ProseMirror a {
          color: #c5a059;
          text-decoration: underline;
        }
      `}</style>
    </div>
  );
}

